/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RxCross1 } from 'react-icons/rx';
import axios from 'axios';
import { toast } from 'react-toastify';
import moment from 'moment';

const API_BASE = 'https://pricemaster.pillup.com';
const AUTH_HEADER = { Authorization: 'pm-pillup' };
const POLL_INTERVAL = 4000;

const PHASE_OPTIONS = [
    { label: 'Both', value: null },
    { label: 'Phase 1', value: 1 },
    { label: 'Phase 2', value: 2 },
];

const STAGE_LABELS = {
    starting: 'Starting…',
    connecting: 'Connecting…',
    phase1: 'Phase 1…',
    phase2: 'Phase 2…',
    done: 'Done',
};

const STATUS_STYLES = {
    running: 'bg-blue-500',
    completed: 'bg-green-500',
    failed: 'bg-red-500',
};

function relativeTime(iso) {
    return moment(iso).fromNow();
}

function duration(start, end) {
    const ms = moment(end).diff(moment(start));
    const d = moment.duration(ms);
    const mins = Math.floor(d.asMinutes());
    const secs = d.seconds();
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

function ParamsSummary({ params }) {
    if (!params) return null;
    const parts = [];
    const phaseLabel = params.phase === 1 ? '1' : params.phase === 2 ? '2' : 'Both';
    parts.push(`Phase: ${phaseLabel}`);
    parts.push(`Limit: ${params.limit ?? 'All'}`);
    parts.push(`${params.threads} threads`);
    parts.push(`${params.delay}s delay`);
    return <span className="text-xs text-gray-500">{parts.join(' · ')}</span>;
}

function PhaseResult({ label, data }) {
    if (!data) return null;
    if (data.skipped) {
        return (
            <div className="text-xs text-gray-500 italic">
                {label}: Skipped — {data.reason}
            </div>
        );
    }
    if (label === 'Phase 1') {
        return (
            <div className="text-xs text-gray-700">
                <span className="font-medium">{label}:</span>{' '}
                {data.success_100 ?? 0} matched, {data.low_score ?? 0} low score,{' '}
                {data.no_match ?? 0} no match, {data.error ?? 0} errors
            </div>
        );
    }
    return (
        <div className="text-xs text-gray-700">
            <span className="font-medium">{label}:</span>{' '}
            {data.success ?? 0} scraped, {data.error ?? 0} errors
        </div>
    );
}

function JobCard({ job }) {
    const [expanded, setExpanded] = useState(false);
    const isRunning = job.status === 'running';
    const isDone = job.status === 'completed' || job.status === 'failed';

    return (
        <div
            className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                isRunning ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => isDone && setExpanded((p) => !p)}
        >
            <div className="flex items-center gap-2">
                <span
                    className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${STATUS_STYLES[job.status] || 'bg-gray-400'} ${
                        isRunning ? 'animate-pulse' : ''
                    }`}
                />
                <span className="text-sm font-medium capitalize">{job.status}</span>
                {isRunning && job.stage && (
                    <span className="text-xs text-blue-600 font-medium">
                        {STAGE_LABELS[job.stage] || job.stage}
                    </span>
                )}
                {job.params?.dryRun && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                        Dry Run
                    </span>
                )}
                <span className="ml-auto text-xs text-gray-400">
                    {isRunning && job.startedAt && `started ${relativeTime(job.startedAt)}`}
                    {job.status === 'completed' && job.startedAt && job.finishedAt &&
                        `completed in ${duration(job.startedAt, job.finishedAt)}`}
                    {job.status === 'failed' && job.startedAt && (
                        job.finishedAt
                            ? `failed after ${duration(job.startedAt, job.finishedAt)}`
                            : `started ${relativeTime(job.startedAt)}`
                    )}
                </span>
            </div>

            <div className="mt-1">
                <ParamsSummary params={job.params} />
            </div>

            {expanded && isDone && (
                <div className="mt-2 pt-2 border-t border-gray-200 flex flex-col gap-1">
                    {job.result ? (
                        <>
                            <PhaseResult label="Phase 1" data={job.result.phase1} />
                            <PhaseResult label="Phase 2" data={job.result.phase2} />
                        </>
                    ) : job.status === 'failed' && job.error ? (
                        <div className="text-xs text-red-600">{job.error}</div>
                    ) : (
                        <div className="text-xs text-gray-400 italic">No result data</div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function BatchScrapeModal({ isOpen, onClose }) {
    const [phase, setPhase] = useState(null);
    const [limit, setLimit] = useState(100);
    const [threads, setThreads] = useState(5);
    const [delay, setDelay] = useState(0.5);
    const [dryRun, setDryRun] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [jobs, setJobs] = useState([]);
    const [loadingJobs, setLoadingJobs] = useState(false);
    const pollRef = useRef(null);

    const fetchJobs = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/api/batchScrape/status`, {
                headers: AUTH_HEADER,
            });
            if (data.success) setJobs(data.jobs || []);
        } catch {
            /* silent — jobs list is best-effort */
        }
    }, []);

    const hasRunning = jobs.some((j) => j.status === 'running');

    useEffect(() => {
        if (!isOpen) return;
        setLoadingJobs(true);
        fetchJobs().finally(() => setLoadingJobs(false));
    }, [isOpen, fetchJobs]);

    useEffect(() => {
        if (!isOpen) return;
        if (hasRunning) {
            pollRef.current = setInterval(fetchJobs, POLL_INTERVAL);
        }
        return () => clearInterval(pollRef.current);
    }, [isOpen, hasRunning, fetchJobs]);

    const handleStart = async () => {
        setSubmitting(true);
        try {
            const body = {
                phase,
                limit: limit === '' ? null : Number(limit),
                threads: Number(threads),
                delay: Number(delay),
                dryRun,
            };
            const { data } = await axios.post(`${API_BASE}/api/batchScrape`, body, {
                headers: { ...AUTH_HEADER, 'Content-Type': 'application/json' },
            });
            if (data.success) {
                toast.success(`Job started: ${data.jobId?.slice(0, 8)}…`);
                await fetchJobs();
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to start batch job');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            id="batch-scrape-backdrop"
            onClick={(e) => e.target.id === 'batch-scrape-backdrop' && onClose()}
            className="w-screen h-screen fixed inset-0 z-[200000] bg-black/50 flex items-center justify-center"
        >
            <div className="bg-white-50 rounded-xl shadow-2xl w-[95vw] max-w-[920px] max-h-[88vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Batch Scrape</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <RxCross1 size={18} />
                    </button>
                </div>

                {/* Body — two columns */}
                <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
                    {/* Left: Form */}
                    <div className="md:w-[38%] p-5 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col gap-4 overflow-y-auto">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                            Start Batch Job
                        </h3>

                        {/* Phase */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-gray-600">Phase</label>
                            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                                {PHASE_OPTIONS.map((opt) => (
                                    <button
                                        key={String(opt.value)}
                                        type="button"
                                        onClick={() => setPhase(opt.value)}
                                        className={`flex-1 text-xs py-1.5 font-medium transition-colors ${
                                            phase === opt.value
                                                ? 'bg-blue-500 text-white-50'
                                                : 'bg-white-50 text-gray-600 hover:bg-gray-50'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Limit */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-gray-600">Limit</label>
                            <input
                                type="number"
                                min="1"
                                placeholder="All items"
                                value={limit}
                                onChange={(e) => setLimit(e.target.value)}
                                className="rounded-lg border border-gray-300 h-8 text-sm px-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                            />
                        </div>

                        {/* Threads + Delay row */}
                        <div className="flex gap-3">
                            <div className="flex-1 flex flex-col gap-1.5">
                                <label className="text-xs font-medium text-gray-600">Threads</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="20"
                                    value={threads}
                                    onChange={(e) => setThreads(e.target.value)}
                                    className="rounded-lg border border-gray-300 h-8 text-sm px-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                                />
                            </div>
                            <div className="flex-1 flex flex-col gap-1.5">
                                <label className="text-xs font-medium text-gray-600">Delay (s)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={delay}
                                    onChange={(e) => setDelay(e.target.value)}
                                    className="rounded-lg border border-gray-300 h-8 text-sm px-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                                />
                            </div>
                        </div>

                        {/* Dry Run */}
                        <label className="flex items-center gap-2.5 cursor-pointer">
                            <div
                                onClick={() => setDryRun((p) => !p)}
                                className={`relative w-9 h-5 rounded-full transition-colors ${
                                    dryRun ? 'bg-blue-500' : 'bg-gray-300'
                                }`}
                            >
                                <div
                                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white-50 rounded-full shadow transition-transform ${
                                        dryRun ? 'translate-x-4' : ''
                                    }`}
                                />
                            </div>
                            <span className="text-xs font-medium text-gray-600">Dry Run</span>
                        </label>

                        {/* Submit */}
                        <button
                            type="button"
                            disabled={submitting}
                            onClick={handleStart}
                            className="mt-auto w-full py-2 rounded-lg bg-blue-500 text-white-50 text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors"
                        >
                            {submitting ? 'Starting…' : 'Start Batch'}
                        </button>
                    </div>

                    {/* Right: Jobs list */}
                    <div className="md:w-[62%] p-5 flex flex-col gap-3 overflow-y-auto">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                            Jobs
                            {hasRunning && (
                                <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                            )}
                        </h3>

                        {loadingJobs && jobs.length === 0 && (
                            <div className="text-sm text-gray-400 py-8 text-center">Loading jobs…</div>
                        )}

                        {!loadingJobs && jobs.length === 0 && (
                            <div className="text-sm text-gray-400 py-8 text-center">
                                No batch jobs yet. Start one from the left panel.
                            </div>
                        )}

                        <div className="flex flex-col gap-2">
                            {jobs.map((job) => (
                                <JobCard key={job.jobId} job={job} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
